import { useId, useState } from "react";
import { ChevronRight, Ruler, X } from "lucide-react";
import {
  DIMENSION_UNIT_ABBREVIATION,
  DIMENSION_UNIT_LABEL,
  WEIGHT_UNIT_ABBREVIATION,
  type DimensionUnit,
  type Item,
  type ItemOverrideField,
  type QuoteLineItemOverrides,
} from "@forgepoint/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ITEM_OVERRIDE_FIELD_LABELS } from "@/modules/quotes/itemOverrideFieldLabels";
import { LineDimensionUnitSelect } from "./LineDimensionUnitSelect";

const DECIMAL_INPUT = /^(\d+\.?\d*|\.\d+)$/;

interface OverrideField {
  key: ItemOverrideField;
  measure: "dimension" | "weight";
}

const OVERRIDE_FIELDS: OverrideField[] = [
  { key: "width", measure: "dimension" },
  { key: "length", measure: "dimension" },
  { key: "height", measure: "dimension" },
  { key: "allowance", measure: "dimension" },
  { key: "weight", measure: "weight" },
];

const MISSING_UNIT_TITLE: Record<OverrideField["measure"], string> = {
  dimension: "Set a dimension unit on the item",
  weight: "Set a weight unit on the item",
};

interface LineItemOverridesCollapsibleProps {
  value: QuoteLineItemOverrides | null | undefined;
  onChange: (next: QuoteLineItemOverrides | null) => void;
  dimensionUnit: DimensionUnit | null | undefined;
  onDimensionUnitChange: (next: DimensionUnit | null) => void;
  catalogItem: Item | null;
  effectiveItem: Item | null;
  enabledFields: ItemOverrideField[];
  dimensionUnitEnabled: boolean;
  readOnly?: boolean;
}

/**
 * Collapsible inputs for a quote line's item field overrides. Sizes are in the
 * line's dimension unit (picked here, else the item's); weight is in the
 * item's. Fields the item has set show as read-only text; a stored line value
 * the line can no longer override stays visible with a clear button.
 */
export function LineItemOverridesCollapsible({
  value,
  onChange,
  dimensionUnit,
  onDimensionUnitChange,
  catalogItem,
  effectiveItem,
  enabledFields,
  dimensionUnitEnabled,
  readOnly = false,
}: LineItemOverridesCollapsibleProps) {
  const idPrefix = useId();
  const storedFields = OVERRIDE_FIELDS.filter((field) => value?.[field.key]);
  const visibleFields = OVERRIDE_FIELDS.filter(
    (field) => enabledFields.includes(field.key) || value?.[field.key],
  );
  const hasValues = storedFields.length > 0 || !!dimensionUnit;
  const [open, setOpen] = useState(hasValues);

  if (
    (visibleFields.length === 0 && !dimensionUnit) ||
    (!hasValues && (readOnly || !catalogItem))
  ) {
    return null;
  }

  const handleFieldChange = (key: ItemOverrideField, text: string) => {
    if (text !== "" && !DECIMAL_INPUT.test(text)) {
      return;
    }
    onChange(withOverride(value, key, text));
  };
  const lineUnits = {
    dimensionUnit: dimensionUnit ?? catalogItem?.dimensionUnit,
    weightUnit: catalogItem?.weightUnit,
  };
  const itemDimensionUnit = catalogItem?.dimensionUnit;
  const hasDimensionField = visibleFields.some(
    (field) => field.measure === "dimension",
  );
  const canPickDimensionUnit =
    !readOnly &&
    !!itemDimensionUnit &&
    hasDimensionField &&
    dimensionUnitEnabled;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mx-2 mt-0.5">
      <CollapsibleTrigger className="group flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground">
        <ChevronRight
          className={cn(
            "h-3 w-3 shrink-0 transition-transform duration-200 ease-out motion-reduce:transition-none",
            open && "rotate-90",
          )}
        />
        <Ruler className="h-3 w-3 shrink-0" />
        <span>Line dimensions per each</span>
        {hasValues && (
          <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
            Overridden
          </Badge>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden px-2 pb-1 pt-1 data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        {canPickDimensionUnit && (
          <LineDimensionUnitSelect
            id={`${idPrefix}-dimension-unit`}
            value={dimensionUnit}
            itemDimensionUnit={itemDimensionUnit}
            onChange={onDimensionUnitChange}
          />
        )}
        {!readOnly && dimensionUnit && !canPickDimensionUnit && (
          <div className="mb-2 space-y-1">
            <span className="block text-xs leading-none text-muted-foreground">
              {ITEM_OVERRIDE_FIELD_LABELS.dimensionUnit}
            </span>
            <StaleLineValue
              label={ITEM_OVERRIDE_FIELD_LABELS.dimensionUnit}
              lineValue={DIMENSION_UNIT_LABEL[dimensionUnit]}
              unit={null}
              note="Clear the line value: the dimension unit can no longer be overridden."
              onClear={() => onDimensionUnitChange(null)}
            />
          </div>
        )}
        {readOnly ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
            {dimensionUnit && (
              <span className="flex items-center gap-1">
                <span className="text-muted-foreground/70">
                  {ITEM_OVERRIDE_FIELD_LABELS.dimensionUnit}:
                </span>
                <span className="text-foreground">
                  {DIMENSION_UNIT_LABEL[dimensionUnit]}
                </span>
              </span>
            )}
            {storedFields.map((field) => (
              <span key={field.key} className="flex items-center gap-1">
                <span className="text-muted-foreground/70">
                  {ITEM_OVERRIDE_FIELD_LABELS[field.key]}:
                </span>
                <span className="tabular-nums text-foreground">
                  {value?.[field.key]}{" "}
                  {unitAbbreviation(lineUnits, field.measure)}
                </span>
              </span>
            ))}
          </div>
        ) : (
          <div className="grid max-w-2xl grid-cols-2 gap-2 @[640px]/quote-items:grid-cols-4">
            {visibleFields.map((field) => {
              const inputId = `${idPrefix}-${field.key}`;
              const label = ITEM_OVERRIDE_FIELD_LABELS[field.key];
              const unit = unitAbbreviation(lineUnits, field.measure);
              const catalogValue = catalogItem?.[field.key];
              const fieldValue = value?.[field.key] ?? "";
              const clearField = () => handleFieldChange(field.key, "");
              if (catalogValue) {
                return (
                  <div key={field.key} className="space-y-1">
                    <span className="block text-xs leading-none text-muted-foreground">
                      {label}
                    </span>
                    <span className="flex h-8 items-center text-xs tabular-nums text-foreground sm:text-sm">
                      {effectiveItem?.[field.key]}{" "}
                      {unitAbbreviation(effectiveItem, field.measure)}
                    </span>
                    {fieldValue && (
                      <StaleLineValue
                        label={label}
                        lineValue={fieldValue}
                        unit={unit}
                        note="Clear the line value: the item now has one."
                        onClear={clearField}
                      />
                    )}
                  </div>
                );
              }
              if (!enabledFields.includes(field.key)) {
                return (
                  <div key={field.key} className="space-y-1">
                    <span className="block text-xs leading-none text-muted-foreground">
                      {label}
                    </span>
                    <StaleLineValue
                      label={label}
                      lineValue={fieldValue}
                      unit={unit}
                      note="Clear the line value: this field can no longer be overridden."
                      onClear={clearField}
                    />
                  </div>
                );
              }
              return (
                <div
                  key={field.key}
                  className="space-y-1"
                  title={unit ? undefined : MISSING_UNIT_TITLE[field.measure]}
                >
                  <Label
                    htmlFor={inputId}
                    className="text-xs font-normal text-muted-foreground"
                  >
                    {label}
                  </Label>
                  <div className="relative">
                    <Input
                      id={inputId}
                      type="text"
                      inputMode="decimal"
                      value={fieldValue}
                      disabled={!unit}
                      onChange={(e) =>
                        handleFieldChange(field.key, e.target.value)
                      }
                      className="h-8 pr-16 text-xs sm:text-sm tabular-nums"
                    />
                    <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
                      {unit && (
                        <span className="pointer-events-none text-xs text-muted-foreground">
                          {unit}
                        </span>
                      )}
                      {fieldValue && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Clear ${label.toLowerCase()} override`}
                          className="h-6 w-6"
                          onClick={clearField}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

interface StaleLineValueProps {
  label: string;
  lineValue: string;
  unit: string | null;
  note: string;
  onClear: () => void;
}

/**
 * A stored line value the line can no longer override, with a clear button
 * and a note saying why it must be cleared.
 */
function StaleLineValue({
  label,
  lineValue,
  unit,
  note,
  onClear,
}: StaleLineValueProps) {
  return (
    <div className="space-y-0.5 text-xs">
      <div className="flex items-center gap-1">
        <span className="tabular-nums text-muted-foreground">
          Line: {lineValue} {unit}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Clear ${label.toLowerCase()} line value`}
          className="h-6 w-6"
          onClick={onClear}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      <p className="text-destructive">{note}</p>
    </div>
  );
}

/**
 * Returns the overrides with one field set or cleared; null when none remain.
 */
function withOverride(
  value: QuoteLineItemOverrides | null | undefined,
  key: ItemOverrideField,
  text: string,
): QuoteLineItemOverrides | null {
  const next = Object.fromEntries(
    OVERRIDE_FIELDS.map((field) => [
      field.key,
      field.key === key ? text : (value?.[field.key] ?? ""),
    ]).filter(([, fieldValue]) => fieldValue !== ""),
  );
  return Object.keys(next).length > 0 ? next : null;
}

/**
 * Returns the unit abbreviation for a measure, or null when no unit is set
 * for it.
 */
function unitAbbreviation(
  units: Pick<Item, "dimensionUnit" | "weightUnit"> | null,
  measure: OverrideField["measure"],
): string | null {
  if (measure === "weight") {
    return units?.weightUnit
      ? WEIGHT_UNIT_ABBREVIATION[units.weightUnit]
      : null;
  }
  return units?.dimensionUnit
    ? DIMENSION_UNIT_ABBREVIATION[units.dimensionUnit]
    : null;
}
